import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Avatar, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatPhone } from "../../lib/format";
import { useCreatePartRequest, useProducts } from "../../lib/queries";
import type { PartRequestCard, PartRequestSummary } from "../../lib/types";
import { ContactPicker } from "./CaseDialog";
import { partRequestNumber } from "./case-text";

type Picked = { id: string; name: string | null; waId: string };

export type PartRequestDraft = Pick<
  PartRequestCard,
  "partId" | "partName" | "productId" | "model" | "quantity" | "description" | "reference"
>;

export const EMPTY_PART_REQUEST: PartRequestDraft = {
  partId: null,
  partName: "",
  productId: null,
  model: null,
  quantity: 1,
  description: null,
  reference: null,
};

const label = (p: { name: string; model: string | null }) => (p.model ? `${p.model} · ${p.name}` : p.name);

/** What is asked for: the same fields when registering a request and on its page. */
export function PartRequestFields({
  value: d,
  onChange,
  idPrefix,
}: {
  value: PartRequestDraft;
  onChange: (d: PartRequestDraft) => void;
  idPrefix: string;
}) {
  const catalog = useProducts("", "", { withInactive: true }).data?.items ?? [];
  const parts = catalog.filter((p) => p.kind === "part");
  const products = catalog.filter((p) => p.kind === "product");
  const set = <K extends keyof PartRequestDraft>(k: K, v: PartRequestDraft[K]) => onChange({ ...d, [k]: v });
  const txt = (k: keyof PartRequestDraft) => (d[k] as string | null) ?? "";
  const id = (name: string) => `${idPrefix}-${name}`;

  // Picking a catalog part names it and, when it fits a single product, fills that in.
  function pickPart(partId: string) {
    const p = parts.find((x) => x.id === partId);
    if (!p) return onChange({ ...d, partId: null });
    onChange({
      ...d,
      partId: p.id,
      partName: d.partName.trim() ? d.partName : p.name,
      productId: d.productId ?? (p.fits.length === 1 ? (p.fits[0] ?? null) : null),
    });
  }

  return (
    <div className="case-fields">
      {parts.length > 0 && (
        <div className="field">
          <label htmlFor={id("part")}>Repuesto del catálogo</label>
          <select className="select" id={id("part")} value={d.partId ?? ""} onChange={(e) => pickPart(e.target.value)}>
            <option value="">No está en el catálogo</option>
            {parts.map((p) => (
              <option key={p.id} value={p.id}>
                {label(p)}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("name")}>Qué pieza</label>
          <input
            className="input"
            id={id("name")}
            placeholder="Ej.: Vaso de vidrio"
            required
            maxLength={200}
            value={d.partName}
            onChange={(e) => set("partName", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={id("quantity")}>Cantidad</label>
          <input
            className="input"
            id={id("quantity")}
            type="number"
            min={1}
            max={999}
            placeholder="Ej.: 1"
            value={d.quantity}
            onChange={(e) => set("quantity", Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      </div>
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("product")}>Para qué producto</label>
          <select
            className="select"
            id={id("product")}
            value={d.productId ?? ""}
            onChange={(e) => set("productId", e.target.value || null)}
          >
            <option value="">No está en el catálogo</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {label(p)}
              </option>
            ))}
          </select>
        </div>
        {!d.productId && (
          <div className="field">
            <label htmlFor={id("model")}>Modelo del cliente</label>
            <input
              className="input"
              id={id("model")}
              placeholder="Ej.: LV-2 (modelo viejo)"
              maxLength={120}
              value={txt("model")}
              onChange={(e) => set("model", e.target.value)}
            />
          </div>
        )}
      </div>
      <div className="field">
        <label htmlFor={id("reference")}>Referencia interna (opcional)</label>
        <input
          className="input mono"
          id={id("reference")}
          placeholder="Ej.: Pedido al proveedor 1187"
          maxLength={120}
          value={txt("reference")}
          onChange={(e) => set("reference", e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={id("description")}>Detalle (opcional)</label>
        <textarea
          className="textarea"
          id={id("description")}
          rows={2}
          placeholder="Ej.: El cliente manda foto de la pieza; color blanco"
          maxLength={4000}
          value={txt("description")}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>
    </div>
  );
}

/**
 * Registers a part request. From a chat or a case the customer is known and the team
 * stays there; from the list, the customer is picked and the new request opens.
 */
export function PartRequestDialog({
  contact,
  conversationId,
  caseId,
  productId,
  model,
  onClose,
}: {
  contact?: Picked;
  conversationId?: string;
  /** The case that waits for the part. */
  caseId?: string;
  productId?: string | null;
  model?: string | null;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const create = useCreatePartRequest();
  const toast = useToast();
  const navigate = useNavigate();
  const [who, setWho] = useState<Picked | null>(contact ?? null);
  const [d, setD] = useState<PartRequestDraft>({
    ...EMPTY_PART_REQUEST,
    productId: productId ?? null,
    model: productId ? null : (model ?? null),
  });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!who) return;
    setError(null);
    try {
      const created = await create.mutateAsync({
        ...d,
        partName: d.partName.trim(),
        contactId: who.id,
        conversationId: conversationId ?? null,
        caseId: caseId ?? null,
      });
      dialog.current?.close();
      if (contact) toast(`Solicitud ${partRequestNumber(created.number)} registrada`);
      else navigate({ to: "/postventa/repuestos/$requestId", params: { requestId: created.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar la solicitud.");
    }
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="part-new-title" className="case-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="part-new-title">Pedir repuesto</h3>
        {who ? (
          <div className="case-who">
            <Avatar name={who.name} fallback={who.waId} />
            <span className="entity-main">
              <span className="entity-name">{who.name ?? "Sin nombre"}</span>
              <span className="entity-meta mono">{formatPhone(who.waId)}</span>
            </span>
            {!contact && (
              <button className="btn btn-sm" type="button" onClick={() => setWho(null)}>
                Cambiar
              </button>
            )}
          </div>
        ) : (
          <ContactPicker onPick={setWho} idPrefix="part-new" />
        )}
        <PartRequestFields value={d} onChange={setD} idPrefix="part-new" />
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={create.isPending || !who || !d.partName.trim()}>
            {create.isPending ? "Registrando…" : "Registrar solicitud"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Part requests as compact links (a case's, a customer's). */
export function PartRequestLinks({ items }: { items: PartRequestSummary[] }) {
  return (
    <ul className="contact-cases-list">
      {items.map((r) => (
        <li key={r.id}>
          <Link to="/postventa/repuestos/$requestId" params={{ requestId: r.id }} className="contact-case">
            <span className="mono">{partRequestNumber(r.number)}</span>
            <span className="contact-case-title">
              {r.quantity > 1 ? `${r.quantity} × ` : ""}
              {r.partName}
            </span>
            <span className={`pill ${r.closed ? "pill-closed" : r.arrived ? "pill-bot" : "pill-human"}`}>
              {r.statusLabel}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
